import { Flex, Box, Text } from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  cohortCadenceLabelFormatter,
  CohortConfig,
  ProfitsVintageTableRow,
} from '../shared/portfolioChartUtils';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import { formatBaseFieldValue } from 'packages/common-frontend/utils';

type ProfitLossVintageTableProps = {
  tableData: ProfitsVintageTableRow[];
  selectedCohortConfig: CohortConfig;
  maxSeriesAge: number;
  fieldType: CreditAgreement.DataTapeField.Type;
  showOriginatedReceivablesCount: boolean;
};
export const ProfitLossVintageTable = ({
  tableData,
  selectedCohortConfig,
  maxSeriesAge,
  fieldType,
  showOriginatedReceivablesCount,
}: ProfitLossVintageTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns: ColumnDef<any, any>[] = [
    {
      id: 'origination',
      accessorKey: 'originationDate',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Origination
          </Text>
        );
      },
      // eslint-disable-next-line react/display-name
      cell: ({ row, getValue }) => {
        const originationDate = cohortCadenceLabelFormatter(
          selectedCohortConfig.cohortBaseCadence,
          getValue(),
        );
        return (
          <Flex alignItems="center">
            <Box
              height="16px"
              width="16px"
              mr="10px"
              bgColor={row.original.color}
            />
            <Text>{originationDate}</Text>
          </Flex>
        );
      },
      size: 200,
    },
    ...(showOriginatedReceivablesCount
      ? [
          {
            id: 'originatedReceivablesCount',
            accessorKey: 'originatedReceivablesCount',
            header: function formatHeader() {
              return (
                <Text color="black" fontSize="16px">
                  Total Receivables Count
                </Text>
              );
            },
            size: 200,
          },
        ]
      : []),
    ...Array(maxSeriesAge)
      .fill(0)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      .map((_, cohortAge) => {
        const columnId = `${cohortAge} ${
          selectedCohortConfig.cohortSeriesCadence
        }${cohortAge === 1 ? '' : 's'}`;
        return {
          id: columnId,
          accessorKey: columnId,
          /* eslint-disable react/display-name */
          header: () => {
            return (
              <Text color="black" fontSize="16px">
                {columnId}
              </Text>
            );
          },
          cell: ({ getValue }: { getValue: any }) => {
            if (getValue() === undefined) {
              return <></>;
            }
            return formatBaseFieldValue(getValue(), fieldType);
          },
          size: 160,
        };
      }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      frozenColumnIds={[
        'origination',
        ...(showOriginatedReceivablesCount
          ? ['originatedReceivablesCount']
          : []),
      ]}
    />
  );
};
